'use client';

import type { MouseEvent } from 'react';
import { cloneElement, forwardRef, useEffect, useState } from 'react';

import {
  baseClasses,
  contentWrapperClasses,
  dismissWrapperClasses,
  iconWrapperClasses,
  msgClasses,
  variantClasses,
  variantIconClasses
} from './Alert.classes';
import type { AlertProps, AlertVariant } from './Alert.types';

import CheckFilledSmallIcon from 'components/common/IconsV2/CheckFilledSmallIcon';
import CloseSmallIcon from 'components/common/IconsV2/CloseSmallIcon';
import ErrorFilledSmallIcon from 'components/common/IconsV2/ErrorFilledSmallIcon';
import InfoFilledSmallIcon from 'components/common/IconsV2/InfoFilledSmallIcon';
import WarningFilledSmallIcon from 'components/common/IconsV2/WarningFilledSmallIcon';
import { cn } from 'helpers/classnames';
import IconButton from 'components/common/IconButton';
import useMartyContext from 'hooks/useMartyContext';

const ICON_SIZE = 16;

const ICON_VARIANT_MAP: Record<AlertVariant, React.ReactElement<any>> = {
  error: <ErrorFilledSmallIcon size={ICON_SIZE} />,
  info: <InfoFilledSmallIcon size={ICON_SIZE} />,
  success: <CheckFilledSmallIcon size={ICON_SIZE} />,
  warning: <WarningFilledSmallIcon size={ICON_SIZE} />
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>((props: AlertProps, ref) => {
  const {
    className,
    msgClassName,
    variant = 'info',
    msg,
    isDismissible = true,
    children,
    onDismiss,
    isShown: isShownManual = true,
    testId,
    ...forwardProps
  } = props;
  const [isShown, setIsShown] = useState(isShownManual);
  const iconClasses = variantIconClasses[variant];
  const { testId: getTestId } = useMartyContext();

  const handleDismiss = (event: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) => {
    setIsShown(false);
    onDismiss?.(event);
  };

  useEffect(() => setIsShown(isShownManual), [isShownManual]);

  if (!isShown) {
    return null;
  }

  return (
    <div role="alert" className={cn(baseClasses, variantClasses[variant], className)} ref={ref} data-test-id={getTestId(testId)} {...forwardProps}>
      <div className={iconWrapperClasses}>{cloneElement(ICON_VARIANT_MAP[variant], { className: iconClasses })}</div>
      <div className={cn(contentWrapperClasses, { ['mr-3']: !isDismissible })}>
        {msg && <p className={cn(msgClasses, msgClassName)}>{msg}</p>}
        {children && <span>{children}</span>}
      </div>
      {isDismissible && (
        <div className={dismissWrapperClasses}>
          <IconButton label="Dismiss" variant="icon" size="base" className="h-6 w-6 min-w-6" onClick={handleDismiss}>
            <CloseSmallIcon size={ICON_SIZE} />
          </IconButton>
        </div>
      )}
    </div>
  );
});

Alert.displayName = 'Alert';
